import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Calendar, Clock, MapPin, User, ExternalLink, Play, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkshopDetail() {
  const { id } = useParams();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkshop();
  }, [id]);

  const fetchWorkshop = async () => {
    try {
      const response = await axios.get(`${API}/workshops/${id}`);
      setWorkshop(response.data);
    } catch (error) {
      console.error("Error fetching workshop:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple animate-spin" />
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-400 mb-4">Workshop not found</p>
        <Link to="/workshops" className="text-purple-light hover:text-white">
          Back to Workshops
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/workshops" 
            data-testid="back-to-workshops"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Workshops
          </Link>

          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="flex items-center gap-2 text-purple-light">
              <Calendar className="w-5 h-5" />
              {workshop.date}
            </span>
            <span className="flex items-center gap-2 text-slate-400">
              <Clock className="w-5 h-5" />
              {workshop.time} ({workshop.duration})
            </span>
            <span className="flex items-center gap-2 text-slate-400">
              <MapPin className="w-5 h-5" />
              {workshop.is_online ? "Online" : workshop.location}
            </span>
          </div>

          <h1 className="font-syne text-4xl md:text-5xl font-bold text-white mb-6">
            {workshop.title}
          </h1>

          <div className="flex items-center gap-3 mb-8">
            <User className="w-5 h-5 text-slate-400" />
            <span className="text-slate-300">Instructor: <span className="text-white">{workshop.instructor}</span></span>
          </div>

          {workshop.is_sponsored && (
            <div className="mb-8 p-4 bg-purple/10 border border-purple/30">
              <p className="text-purple-light text-sm uppercase tracking-wider">
                Sponsored by {workshop.sponsor_name}
              </p>
            </div>
          )}

          <p className="text-lg text-slate-300 leading-relaxed mb-8">
            {workshop.description}
          </p>

          <div className="flex flex-wrap gap-4">
            {workshop.replay_url ? (
              <a
                href={workshop.replay_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="watch-replay"
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple text-white font-bold uppercase tracking-widest text-xs rounded-full hover:bg-purple-hover transition-all"
              >
                <Play className="w-4 h-4" />
                Watch Replay
              </a>
            ) : workshop.registration_url ? (
              <a
                href={workshop.registration_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple text-white font-bold uppercase tracking-widest text-xs rounded-full hover:bg-purple-hover transition-all"
              >
                Register Now
                <ExternalLink className="w-4 h-4" />
              </a>
            ) : null}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
